import { useState } from "react";
import { parseMgrsSimple, buildMgrsString, clamp5, milsToRad } from "../utils/fdcHelpers";

/**
 * Manages all pattern distribution state and computation.
 * Extracted from FdcPage to isolate 15+ state variables and 3 compute functions.
 */
export default function usePatternConfig() {
  const [patternAnchor, setPatternAnchor] = useState("original");
  const [patternEnabled, setPatternEnabled] = useState(true);
  const [patternMode, setPatternMode] = useState("free");
  const [patternRadius, setPatternRadius] = useState(200);
  const [patternRotationMils, setPatternRotationMils] = useState(0);
  const [linearBearingMils, setLinearBearingMils] = useState(2600);
  const [linearSpacing, setLinearSpacing] = useState(50);
  const [linearSnapToTicks, setLinearSnapToTicks] = useState(true);
  const [ringStep, setRingStep] = useState(50);
  const [radialStepMils, setRadialStepMils] = useState(200);
  const [showRadialLabels, setShowRadialLabels] = useState(true);
  const [patternPoints, setPatternPoints] = useState({});
  const [patternError, setPatternError] = useState("");
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [snapMeters, setSnapMeters] = useState(5);

  const buildCircleDistribution = (guns) => {
    const N = guns.length;
    if (N <= 0) return {};

    const rot = milsToRad(patternRotationMils);
    const out = {};

    for (let i = 0; i < N; i++) {
      const gunId = String(guns[i].gun_number);
      const a = rot + (i * 2 * Math.PI) / N;
      out[gunId] = {
        dx: patternRadius * Math.sin(a),
        dy: patternRadius * Math.cos(a),
      };
    }

    return out;
  };

  const buildLinearDistribution = (guns) => {
    const N = guns.length;
    if (N <= 0) return {};

    const a = milsToRad(linearBearingMils);
    const ux = Math.sin(a);
    const uy = Math.cos(a);
    const mid = (N - 1) / 2;
    const out = {};

    for (let i = 0; i < N; i++) {
      const gunId = String(guns[i].gun_number);
      const t = (i - mid) * (Number(linearSpacing) || 0);
      out[gunId] = { dx: t * ux, dy: t * uy };
    }

    return out;
  };

  const computeTargetsByGun = (guns, centerMgrs) => {
    const parsed = parseMgrsSimple(centerMgrs);

    if (!parsed) {
      return {
        ok: false,
        error: "Pattern requires center MGRS with zone(5)+10 digits (e.g., 29SND6000050000).",
      };
    }

    const { zone, e: E0, n: N0 } = parsed;
    const targets = {};

    for (const g of guns) {
      const gunId = String(g.gun_number);
      const p = patternPoints[gunId] || { dx: 0, dy: 0 };
      const e5 = clamp5(E0 + p.dx);
      const n5 = clamp5(N0 + p.dy);

      if (e5 === null || n5 === null) {
        return {
          ok: false,
          error: `Pattern for Gun ${gunId} moved outside simple grid bounds. Reduce offsets/radius.`,
        };
      }

      targets[gunId] = buildMgrsString(zone, e5, n5);
    }

    return { ok: true, targets };
  };

  const resetPattern = () => {
    setPatternAnchor("original");
    setPatternPoints({});
    setPatternError("");
  };

  return {
    patternAnchor, setPatternAnchor,
    patternEnabled, setPatternEnabled,
    patternMode, setPatternMode,
    patternRadius, setPatternRadius,
    patternRotationMils, setPatternRotationMils,
    linearBearingMils, setLinearBearingMils,
    linearSpacing, setLinearSpacing,
    linearSnapToTicks, setLinearSnapToTicks,
    ringStep, setRingStep,
    radialStepMils, setRadialStepMils,
    showRadialLabels, setShowRadialLabels,
    patternPoints, setPatternPoints,
    patternError, setPatternError,
    snapEnabled, setSnapEnabled,
    snapMeters, setSnapMeters,
    buildCircleDistribution,
    buildLinearDistribution,
    computeTargetsByGun,
    resetPattern,
  };
}
